import { FC, useEffect, useMemo, useState, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Grid,
  Stack,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  IconButton,
  styled,
  Button,
  Tooltip,
  Divider,
  Alert,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Info as InfoIcon,
  Edit as EditIcon,
  TrendingDown as UndervalIcon,
  TrendingUp as OvervalIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Settings as SettingsIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterListIcon,
  Description as DescriptionIcon,
  FormatListBulleted as ListIcon,
} from "@mui/icons-material";
import { Range } from "react-date-range";
import { format } from "date-fns";

import {
  TextInput,
  SelectBox,
  MultiAutocomplete,
  SingleAutocomplete,
  DateRangeFilter,
  SkeletonLoader,
} from "@/components";
import VirtualizedChipList from "@/components/virtualized-chip-list";
import InputErrorMessage from "@/components/input-error-message";
import {
  ReconciliationFormData,
  AdjustmentType,
  DutyAdjustmentEntry,
  DutyAdjustmentOperation,
  CH99ChangeMode,
  CH99Transformation,
  CombinedAdjustmentData,
  transformFiltersToBackend,
  createEmptyTransformation,
} from "@/types/reconciliation";
import {
  validateReconciliationForm,
  validateHtsCode,
} from "@/utils/reconciliation-validation";
import {
  validateField,
  ValidationRules,
  ValidationPatterns,
  debounce,
} from "@/utils/form-validation";
import { getLastYearToDateRange, formatHTSCode } from "@/utils";
import {
  useGetHTSCodesForDropdownQuery,
  useGetCountriesForDropdownQuery,
  useGetManufacturersForDropdownQuery,
  useGetTotalReconcilationQuery,
} from "@/services";
import { useSnackbar, useEffectiveClientScope } from "@/hooks";
import { tokens } from "@/tokens";

// ─── Design tokens for card sections ───────────────────────────────
const cardSx = {
  border: `1px solid ${tokens.border.default}`,
  borderRadius: tokens.radius.lg,
  overflow: "hidden",
};
const sectionBreak = `1px solid ${tokens.border.default}`;
const cardBodySx = { px: 2, py: 1.5 };

// ─── Section header with icon + uppercase label ───────────────────
const SectionHeader: FC<{
  icon: React.ReactNode;
  label: string;
  suffix?: React.ReactNode;
  actions?: React.ReactNode;
}> = ({ icon, label, suffix, actions }) => (
  <Box
    sx={{
      px: 2,
      py: 1.25,
      bgcolor: tokens.bg.hover,
      borderBottom: `1px solid ${tokens.border.default}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ color: tokens.text.muted, display: "flex", alignItems: "center" }}>{icon}</Box>
      <Typography
        sx={{
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontSize: tokens.fontSize.xs,
          fontWeight: tokens.fontWeight.semibold,
          color: tokens.text.secondary,
        }}
      >
        {label}
      </Typography>
      {suffix && (
        <Typography
          sx={{
            fontSize: tokens.fontSize.xs,
            fontWeight: tokens.fontWeight.normal,
            color: tokens.text.muted,
          }}
        >
          {suffix}
        </Typography>
      )}
    </Stack>
    {actions}
  </Box>
);

// ─── Mode Toggle (pill style) ─────────────────────────────────────
const ModeToggle: FC<{
  value: string;
  options: { value: string; label: string; icon: React.ReactNode }[];
  onChange: (value: string) => void;
}> = ({ value, options, onChange }) => (
  <Stack
    direction="row"
    sx={{
      bgcolor: tokens.bg.muted,
      borderRadius: tokens.radius.full,
      p: 0.5,
      border: `1px solid ${tokens.border.default}`,
      width: "fit-content",
    }}
  >
    {options.map((opt) => (
      <Button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        startIcon={opt.icon}
        sx={{
          borderRadius: tokens.radius.full,
          textTransform: "none",
          fontSize: tokens.fontSize.sm2,
          fontWeight: value === opt.value ? tokens.fontWeight.semibold : tokens.fontWeight.normal,
          py: 0.75,
          px: 2.5,
          minHeight: 36,
          bgcolor: value === opt.value ? tokens.bg.surface : "transparent",
          color: value === opt.value ? tokens.text.primary : tokens.text.secondary,
          boxShadow: value === opt.value ? tokens.shadow.sm : "none",
          "&:hover": { bgcolor: value === opt.value ? tokens.bg.surface : tokens.bg.hover },
        }}
      >
        {opt.label}
      </Button>
    ))}
  </Stack>
);

// ─── Adjustment Type Card ─────────────────────────────────────────
const AdjustmentTypeCard: FC<{
  symbol: string;
  title: string;
  description: string;
  selected: boolean;
  onClick: () => void;
}> = ({ symbol, title, description, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      flex: 1,
      px: 1.5,
      py: 1.25,
      cursor: "pointer",
      borderRadius: tokens.radius.sm,
      border: `1px solid ${selected ? tokens.brand.primary : tokens.border.default}`,
      bgcolor: selected ? tokens.brand.primary : tokens.bg.surface,
      color: selected ? "#fff" : tokens.text.primary,
      transition: "all 0.15s ease",
      "&:hover": {
        borderColor: tokens.brand.primary,
        bgcolor: selected ? tokens.brand.primaryHover : tokens.bg.hover,
      },
    }}
  >
    <Typography
      sx={{
        fontFamily: "'Georgia', serif",
        fontSize: 15,
        fontWeight: 400,
        lineHeight: 1,
        mb: 0.75,
        opacity: selected ? 1 : 0.45,
      }}
    >
      {symbol}
    </Typography>
    <Typography
      sx={{
        fontWeight: tokens.fontWeight.semibold,
        fontSize: tokens.fontSize.xs,
        mb: 0.25,
      }}
    >
      {title}
    </Typography>
    <Typography
      sx={{
        fontSize: tokens.fontSize["2xs"],
        opacity: 0.7,
        lineHeight: 1.3,
      }}
    >
      {description}
    </Typography>
  </Box>
);

// ─── Segmented Toggle ─────────────────────────────────────────────
const SegmentedToggle: FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}> = ({ value, options, onChange }) => (
  <Stack direction="row" sx={{ border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md, overflow: "hidden" }}>
    {options.map((opt) => (
      <Button
        key={opt.value}
        onClick={() => onChange(opt.value)}
        sx={{
          flex: 1,
          borderRadius: 0,
          textTransform: "none",
          fontSize: tokens.fontSize.sm2,
          fontWeight: tokens.fontWeight.medium,
          py: 1,
          px: 2,
          minHeight: 38,
          bgcolor: value === opt.value ? tokens.brand.primary : "transparent",
          color: value === opt.value ? "#fff" : tokens.text.secondary,
          "&:hover": { bgcolor: value === opt.value ? tokens.brand.primaryHover : tokens.bg.hover },
          borderRight: `1px solid ${tokens.border.default}`,
          "&:last-of-type": { borderRight: "none" },
        }}
      >
        {opt.label}
      </Button>
    ))}
  </Stack>
);

// ─── Shared Toggle Header Component ────────────────────────────────
const HTSCodeToggleHeader: FC<{
  codeType: "mfn" | "ch99";
  onCodeTypeChange: (type: "mfn" | "ch99") => void;
}> = ({ codeType, onCodeTypeChange }) => (
  <Box
    sx={{
      position: "sticky",
      top: 0,
      bgcolor: tokens.bg.surface,
      zIndex: 1,
      borderBottom: "1px solid",
      borderColor: tokens.border.default,
      p: 1,
      display: "flex",
      alignItems: "center",
      gap: 1,
    }}
  >
    <Typography variant="body2" sx={{ fontWeight: "bold", mr: 1 }}>
      Code Type:
    </Typography>
    <Button
      size="small"
      variant={codeType === "mfn" ? "contained" : "outlined"}
      onClick={() => onCodeTypeChange("mfn")}
    >
      MFN Codes
    </Button>
    <Button
      size="small"
      variant={codeType === "ch99" ? "contained" : "outlined"}
      onClick={() => onCodeTypeChange("ch99")}
    >
      Chapter 99
    </Button>
  </Box>
);

// HTSCodesWithToggle Component
interface HTSCodesWithToggleProps {
  value: string[];
  onChange: (value: string[]) => void;
  codeType: "mfn" | "ch99";
  onCodeTypeChange: (type: "mfn" | "ch99") => void;
  allOptions: { value: string; label: string; type: string }[];
  isLoading: boolean;
}

const HTSCodesWithToggle: FC<HTSCodesWithToggleProps> = ({
  value,
  onChange,
  codeType,
  onCodeTypeChange,
  allOptions,
  isLoading,
}) => {
  const currentOptions =
    codeType === "mfn"
      ? allOptions.filter((code) => code.type === "hts")
      : allOptions.filter(
          (code) => code.type === "ch99Code" || code.type === "ch99Code2"
        );

  return (
    <MultiAutocomplete
      value={value}
      onChange={onChange}
      label="HTS Codes (supports wildcards: 1234*)"
      options={currentOptions.map((opt) => opt.value)}
      placeholder={isLoading ? "Loading codes..." : "Type HTS codes or select from list..."}
      helperText={`${currentOptions.length} ${codeType === "mfn" ? "MFN" : "Chapter 99"} codes available`}
      customHeader={
        <HTSCodeToggleHeader codeType={codeType} onCodeTypeChange={onCodeTypeChange} />
      }
    />
  );
};

// Styled components
const StyledInputContainer = styled(Stack)({
  flexDirection: "row",
  border: "1.2px solid #DEDEDE",
  borderRadius: "12px",
  padding: 24,
  justifyContent: "space-between",
  alignItems: "center",
});

const numberWithCommas = (num: number): string => {
  return Math.floor(num)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: "$",
    MXN: "$",
    CAD: "$",
    GBP: "\u00a3",
    EUR: "\u20ac",
    CNY: "\u00a5",
    JPY: "\u00a5",
    AUD: "$",
    CHF: "CHF",
  };
  return symbols[currency] || "$";
};

// ─── Line-specific types ───────────────────────────────────────────
interface LineAdjustment {
  entryLine: string;
  entryNumber?: string;
  lineNumber?: number;
  originalValue: number;
  correctValue: number;
  adjustmentAmount: number;
  adjustmentType: "UNDERVALUATION" | "OVERVALUATION" | "MISMATCH";
  isEdited?: boolean;
}

type FormMode = "bulk" | "line_specific";

interface FormData {
  reconFlag: "Y" | "N" | "";
  htsCodes: string[];
  originCodes: string[];
  manufacturerIds: string[];
  entryLines: string[];
  adjustmentType: AdjustmentType;
  adjustmentMode: "percentage" | "amount";
  percentage: string;
  amount: string;
  currency: string;
  dutyAdjustments: DutyAdjustmentEntry[];
}

// ─── Props ─────────────────────────────────────────────────────────
interface StepConfigureProps {
  onSubmit: (data: ReconciliationFormData) => void;
  isLoading: boolean;
  initialEntryLines?: string[];
  initialItems?: ValueDiscrepancyItem[];
}

const StepConfigure: FC<StepConfigureProps> = ({
  onSubmit,
  isLoading: _isLoading,
  initialEntryLines,
  initialItems,
}) => {
  const { show: showSnackbar } = useSnackbar();
  const {
    clientId: effectiveClientId,
    tenantId,
    isTDAdmin,
  } = useEffectiveClientScope();

  // Date range state
  const [dateRange, setDateRange] = useState<Pick<Range, "startDate" | "endDate">>({
    ...getLastYearToDateRange(),
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    trigger,
  } = useForm<FormData>({
    defaultValues: {
      reconFlag: "",
      htsCodes: [],
      originCodes: [],
      manufacturerIds: [],
      entryLines: [],
      adjustmentType: "value_percentage",
      adjustmentMode: "percentage",
      percentage: "",
      amount: "",
      currency: "USD",
      dutyAdjustments: [],
    },
    mode: "onChange",
  });

  const adjustmentType = watch("adjustmentType");
  const adjustmentMode = watch("adjustmentMode");
  const amount = watch("amount");
  const percentage = watch("percentage");
  const currency = watch("currency");
  const reconFlag = watch("reconFlag");
  const htsCodes = watch("htsCodes");
  const originCodes = watch("originCodes");
  const manufacturerIds = watch("manufacturerIds");
  const entryLines = watch("entryLines");
  const dutyAdjustments = watch("dutyAdjustments");

  // Validation
  const formValidationRules: ValidationRules = useMemo(
    () => ({
      percentage:
        adjustmentType === "value_percentage" && adjustmentMode === "percentage"
          ? {
              required: true,
              pattern: ValidationPatterns.percentage,
              message: "Enter a valid percentage (e.g., 5% or -10.5%)",
            }
          : {},
      amount:
        (adjustmentType === "value_percentage" || adjustmentType === "combined_value_and_duty") &&
        adjustmentMode === "amount"
          ? {
              required: true,
              pattern: ValidationPatterns.amount,
              min: 0.01,
              message: "Enter a valid amount greater than 0",
            }
          : {},
      currency: adjustmentMode === "amount" ? { required: true, message: "Currency is required when using amount mode" } : {},
    }),
    [adjustmentType, adjustmentMode]
  );

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [htsValidationErrors, setHtsValidationErrors] = useState<Record<number, string>>({});
  const [entryLineInputValue, setEntryLineInputValue] = useState("");

  const debouncedValidate = useCallback(
    debounce((field: string, value: any) => {
      if (formValidationRules[field]) {
        const error = validateField(value, formValidationRules[field]);
        setFieldErrors((prev) => ({ ...prev, [field]: error || "" }));
      }
    }, 300),
    [formValidationRules]
  );

  const [codeType, setCodeType] = useState<"mfn" | "ch99">("mfn");

  // Line-specific state
  const hasInitialItems = initialItems && initialItems.length > 0;
  const [formMode, setFormMode] = useState<FormMode>(hasInitialItems ? "line_specific" : "bulk");
  const [lineAdjustments, setLineAdjustments] = useState<LineAdjustment[]>([]);

  // Initialize line adjustments from initialItems
  useEffect(() => {
    if (initialItems && initialItems.length > 0) {
      setFormMode("line_specific");

      const adjustments: LineAdjustment[] = [];
      for (const item of initialItems) {
        if (item.perLineAdjustments && item.perLineAdjustments.length > 0) {
          for (const pla of item.perLineAdjustments) {
            adjustments.push({
              entryLine: pla.entryLine || `${item.entryNumber || "UNKNOWN"}-${pla.lineNumber}`,
              entryNumber: item.entryNumber,
              lineNumber: pla.lineNumber,
              originalValue: pla.declaredValue,
              correctValue: pla.actualValue,
              adjustmentAmount: pla.adjustmentAmount,
              adjustmentType: item.discrepancyType as "UNDERVALUATION" | "OVERVALUATION" | "MISMATCH",
              isEdited: false,
            });
          }
        } else if (item.entryLineData && item.entryLineData.length > 1 && item.declaredValue > 0) {
          const ratio = item.actualValue / item.declaredValue;
          for (const eld of item.entryLineData) {
            const goodsValue = eld.goodsValue || 0;
            const adjusted = Math.round(goodsValue * ratio * 100) / 100;
            adjustments.push({
              entryLine: eld.entryLine || `${item.entryNumber || "UNKNOWN"}-${eld.lineNumber}`,
              entryNumber: item.entryNumber,
              lineNumber: eld.lineNumber,
              originalValue: goodsValue,
              correctValue: adjusted,
              adjustmentAmount: Math.round(Math.abs(adjusted - goodsValue) * 100) / 100,
              adjustmentType: item.discrepancyType as "UNDERVALUATION" | "OVERVALUATION" | "MISMATCH",
              isEdited: false,
            });
          }
        } else {
          adjustments.push({
            entryLine: item.entryLine || `${item.entryNumber || "UNKNOWN"}-${item.lineNumber || 0}`,
            entryNumber: item.entryNumber,
            lineNumber: item.lineNumber,
            originalValue: item.declaredValue,
            correctValue: item.actualValue,
            adjustmentAmount: item.discrepancyAmount,
            adjustmentType: item.discrepancyType as "UNDERVALUATION" | "OVERVALUATION" | "MISMATCH",
            isEdited: false,
          });
        }
      }
      setLineAdjustments(adjustments);
    } else {
      setFormMode("bulk");
      setLineAdjustments([]);
    }
  }, [initialItems]);

  // Line adjustment totals
  const lineAdjustmentTotals = useMemo(() => {
    const totalOriginal = lineAdjustments.reduce((sum, adj) => sum + adj.originalValue, 0);
    const totalCorrected = lineAdjustments.reduce((sum, adj) => sum + adj.correctValue, 0);
    const totalAdjustment = lineAdjustments.reduce((sum, adj) => sum + adj.adjustmentAmount, 0);
    return { totalOriginal, totalCorrected, totalAdjustment, count: lineAdjustments.length };
  }, [lineAdjustments]);

  const updateLineAdjustment = useCallback(
    (index: number, field: keyof LineAdjustment, value: any) => {
      setLineAdjustments((prev) => {
        const updated = [...prev];
        if (field === "correctValue") {
          const newCorrectValue = parseFloat(value) || 0;
          updated[index] = {
            ...updated[index],
            correctValue: newCorrectValue,
            adjustmentAmount: newCorrectValue - updated[index].originalValue,
            isEdited: true,
          };
        } else if (field === "adjustmentAmount") {
          const newAdjustment = parseFloat(value) || 0;
          updated[index] = {
            ...updated[index],
            adjustmentAmount: newAdjustment,
            correctValue: updated[index].originalValue + newAdjustment,
            isEdited: true,
          };
        } else {
          (updated[index] as any)[field] = value;
        }
        return updated;
      });
    },
    []
  );

  const addLineAdjustment = useCallback(() => {
    const newLine: LineAdjustment = {
      entryLine: `NEW-${Date.now()}`,
      entryNumber: "",
      lineNumber: undefined,
      originalValue: 0,
      correctValue: 0,
      adjustmentAmount: 0,
      adjustmentType: "MISMATCH",
      isEdited: true,
    };
    setLineAdjustments((prev) => [...prev, newLine]);
  }, []);

  const removeLineAdjustment = useCallback((index: number) => {
    setLineAdjustments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // API queries for dropdowns
  const { data: allHTSCodes = [], isLoading: htsLoading } = useGetHTSCodesForDropdownQuery({
    clientId: effectiveClientId,
    ...(isTDAdmin && tenantId && { tenantId }),
    includeChapter99: true,
  });

  const mfnCodes = allHTSCodes.filter((code) => code.type === "hts");
  const ch99Codes = allHTSCodes.filter(
    (code) => code.type === "ch99Code" || code.type === "ch99Code2"
  );
  const htsOptions = codeType === "mfn" ? mfnCodes : ch99Codes;

  const { data: countryOptions = [] } = useGetCountriesForDropdownQuery(
    {
      clientId: effectiveClientId || "",
      dateFrom: dateRange.startDate ? format(dateRange.startDate, "yyyy-MM-dd") : undefined,
      dateTo: dateRange.endDate ? format(dateRange.endDate, "yyyy-MM-dd") : undefined,
    },
    { skip: !effectiveClientId }
  );

  const { data: manufacturerOptions = [] } = useGetManufacturersForDropdownQuery({
    dashboardVisibility: {
      clientId: effectiveClientId,
      ...(isTDAdmin && tenantId && { tenantId }),
    },
  });

  // Date timestamps for API
  const dateTo = useMemo(() => {
    if (!dateRange.endDate) return undefined;
    return Math.floor(dateRange.endDate.getTime() / 1000);
  }, [dateRange.endDate]);

  const dateFrom = useMemo(() => {
    if (!dateRange.startDate) return undefined;
    return Math.floor(dateRange.startDate.getTime() / 1000);
  }, [dateRange.startDate]);

  // Total sum query for rate factor calculation
  const shouldFetchTotalSum =
    (adjustmentType === "value_percentage" || adjustmentType === "combined_value_and_duty") &&
    currency &&
    dateRange.startDate &&
    dateRange.endDate &&
    effectiveClientId;

  const dutyAdjustmentHTSCodes = useMemo(() => {
    const codes: string[] = [];
    if (
      (adjustmentType === "duty_rate_adjustment" || adjustmentType === "combined_value_and_duty") &&
      dutyAdjustments
    ) {
      dutyAdjustments.forEach((adj) => {
        if (adj.operation === "code_change" && adj.originalCode) {
          codes.push(adj.originalCode);
        } else if (adj.operation === "rate_adjustment" && adj.htsCode) {
          codes.push(adj.htsCode);
        }
      });
    }
    return codes;
  }, [adjustmentType, dutyAdjustments]);

  const queryParams = useMemo(() => {
    const params: any = {
      currency: currency as any,
      clientId: effectiveClientId,
      ...(isTDAdmin && tenantId && { tenantId }),
      dateTo,
      dateFrom,
    };

    const allHTSCodesCombined = [...(htsCodes || []), ...dutyAdjustmentHTSCodes];
    const uniqueHTSCodes = [...new Set(allHTSCodesCombined)];

    const formFilters = {
      reconFlag: reconFlag && (reconFlag === "Y" || reconFlag === "N") ? reconFlag : undefined,
      htsCodes: uniqueHTSCodes.length ? uniqueHTSCodes : undefined,
      originCodes: originCodes?.length ? originCodes : undefined,
      manufacturerIds: manufacturerIds?.length ? manufacturerIds : undefined,
      entryLines: entryLines?.length ? entryLines : undefined,
    };

    const backendFilters = transformFiltersToBackend(formFilters);
    if (Object.keys(backendFilters).length > 0) {
      params.filters = backendFilters;
    }

    return params;
  }, [
    currency,
    effectiveClientId,
    tenantId,
    isTDAdmin,
    dateTo,
    dateFrom,
    reconFlag,
    htsCodes,
    originCodes,
    manufacturerIds,
    entryLines,
    dutyAdjustmentHTSCodes,
  ]);

  const { data: totalSum, isLoading: totalSumLoading } = useGetTotalReconcilationQuery(
    queryParams,
    { skip: !shouldFetchTotalSum }
  );

  const rateFactor = useMemo(() => {
    if (!amount || !totalSum || totalSum === 0) return "0.00";
    const factor = (parseFloat(amount) / totalSum) * 100;
    return factor.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }, [amount, totalSum]);

  const adjustmentAmount = useMemo(() => {
    if (!percentage || !totalSum || totalSum === 0) return 0;
    const percentValue = parseFloat(percentage.replace("%", ""));
    return (totalSum * percentValue) / 100;
  }, [percentage, totalSum]);

  const reconFlagOptions = useMemo(
    () => [
      { value: "Y", label: "Yes - Show only reconciled entries" },
      { value: "N", label: "No - Show only non-reconciled entries" },
    ],
    []
  );

  const adjustmentModeOptions = useMemo(
    () => [
      { value: "percentage", label: "Percentage (e.g., 5% or 0.1%)" },
      { value: "amount", label: "Dollar Amount" },
    ],
    []
  );

  const currencyOptions = useMemo(
    () => [
      { value: "USD", label: "USD - US Dollar" },
      { value: "EUR", label: "EUR - Euro" },
      { value: "GBP", label: "GBP - British Pound" },
      { value: "CAD", label: "CAD - Canadian Dollar" },
      { value: "MXN", label: "MXN - Mexican Peso" },
      { value: "CNY", label: "CNY - Chinese Yuan" },
    ],
    []
  );

  // Duty adjustment CRUD
  const addDutyAdjustment = (operation: DutyAdjustmentOperation = "rate_adjustment") => {
    const newEntry: DutyAdjustmentEntry = {
      operation,
      ...(operation === "code_change"
        ? { originalCode: "", targetCode: "", ch99ChangeMode: undefined, newCH99CodeList: [], ch99Transformations: [] }
        : { htsCode: "", newRate: 0 }),
      ...(hasInitialItems && formMode === "line_specific"
        ? { applyTo: "all" as const, targetEntryLines: [] }
        : {}),
    };
    setValue("dutyAdjustments", [...dutyAdjustments, newEntry]);
    trigger("dutyAdjustments");
  };

  const removeDutyAdjustment = (index: number) => {
    const updatedEntries = dutyAdjustments.filter((_, i) => i !== index);
    setValue("dutyAdjustments", updatedEntries);
    trigger("dutyAdjustments");

    const newErrors: Record<number, string> = {};
    Object.entries(htsValidationErrors).forEach(([key, value]) => {
      const keyIndex = parseInt(key);
      if (keyIndex < index) newErrors[keyIndex] = value;
      else if (keyIndex > index) newErrors[keyIndex - 1] = value;
    });
    setHtsValidationErrors(newErrors);
  };

  const updateDutyAdjustment = (index: number, field: keyof DutyAdjustmentEntry, value: any) => {
    const updatedEntries = [...dutyAdjustments];
    const currentEntry = updatedEntries[index];

    if (field === "operation") {
      const operation = value as DutyAdjustmentOperation;
      updatedEntries[index] = {
        operation,
        ...(operation === "code_change"
          ? { originalCode: "", targetCode: "", ch99ChangeMode: undefined, newCH99CodeList: [], ch99Transformations: [] }
          : { htsCode: "", newRate: 0 }),
        applyTo: currentEntry.applyTo,
        targetEntryLines: currentEntry.targetEntryLines,
      };
    } else {
      (updatedEntries[index] as any)[field] = value;
    }

    setValue("dutyAdjustments", updatedEntries);
    trigger("dutyAdjustments");

    if (field === "originalCode" || field === "targetCode" || field === "htsCode") {
      const validationError = validateHtsCode(value as string);
      setHtsValidationErrors((prev) => ({ ...prev, [index]: validationError || "" }));
    }
  };

  // CH99 Transformation management
  const addTransformation = (adjustmentIndex: number) => {
    const updatedEntries = [...dutyAdjustments];
    const adjustment = updatedEntries[adjustmentIndex];
    if (adjustment.operation === "code_change") {
      if (!adjustment.ch99Transformations) adjustment.ch99Transformations = [];
      adjustment.ch99Transformations.push(createEmptyTransformation());
      setValue("dutyAdjustments", updatedEntries);
      trigger("dutyAdjustments");
    }
  };

  const removeTransformation = (adjustmentIndex: number, transformIndex: number) => {
    const updatedEntries = [...dutyAdjustments];
    const adjustment = updatedEntries[adjustmentIndex];
    if (adjustment.operation === "code_change" && adjustment.ch99Transformations) {
      adjustment.ch99Transformations = adjustment.ch99Transformations.filter(
        (_, i) => i !== transformIndex
      );
      setValue("dutyAdjustments", updatedEntries);
      trigger("dutyAdjustments");
    }
  };

  const updateTransformation = (
    adjustmentIndex: number,
    transformIndex: number,
    field: keyof CH99Transformation,
    value: string
  ) => {
    const updatedEntries = [...dutyAdjustments];
    const adjustment = updatedEntries[adjustmentIndex];
    if (
      adjustment.operation === "code_change" &&
      adjustment.ch99Transformations &&
      adjustment.ch99Transformations[transformIndex]
    ) {
      (adjustment.ch99Transformations[transformIndex] as any)[field] = value;
      setValue("dutyAdjustments", updatedEntries);
      trigger("dutyAdjustments");
    }
  };

  const updateCH99CodeList = (adjustmentIndex: number, codes: string[]) => {
    const updatedEntries = [...dutyAdjustments];
    const adjustment = updatedEntries[adjustmentIndex];
    if (adjustment.operation === "code_change") {
      adjustment.newCH99CodeList = codes;
      setValue("dutyAdjustments", updatedEntries);
      trigger("dutyAdjustments");
    }
  };

  // ─── Form Submit Handler ─────────────────────────────────────────
  const handleFormSubmit = (data: FormData) => {
    // Line-specific mode
    if (formMode === "line_specific") {
      if (lineAdjustments.length === 0) {
        showSnackbar("Validation Error", "No line adjustments to submit", "error");
        return;
      }

      const watchedDutyAdjustments = watch("dutyAdjustments");
      const selectedAdjustmentType = data.adjustmentType;

      if (
        selectedAdjustmentType === "duty_rate_adjustment" ||
        selectedAdjustmentType === "combined_value_and_duty"
      ) {
        if (!watchedDutyAdjustments || watchedDutyAdjustments.length === 0) {
          showSnackbar("Validation Error", "Please add at least one code change", "error");
          return;
        }
        const invalidAdjustments = watchedDutyAdjustments.filter((adj) => {
          if (adj.operation === "code_change") return !adj.originalCode || !adj.targetCode;
          else if (adj.operation === "rate_adjustment")
            return !adj.htsCode || adj.newRate === undefined || adj.newRate === null;
          return false;
        });
        if (invalidAdjustments.length > 0) {
          showSnackbar("Validation Error", "Please complete all required fields in code changes", "error");
          return;
        }
      }

      const lineSpecificFormData: ReconciliationFormData = {
        dateRange: { start: "2000-01-01", end: "2100-12-31" },
        filters: { entryLines: lineAdjustments.map((adj) => adj.entryLine) },
        adjustmentType: "line_specific" as AdjustmentType,
        adjustmentData: {
          lineAdjustments: lineAdjustments.map((adj) => ({
            entryLine: adj.entryLine,
            entryNumber: adj.entryNumber,
            lineNumber: adj.lineNumber,
            originalValue: adj.originalValue,
            correctValue: adj.correctValue,
            adjustmentAmount: adj.adjustmentAmount,
            adjustmentType: adj.adjustmentType,
          })),
          ...((selectedAdjustmentType === "duty_rate_adjustment" ||
            selectedAdjustmentType === "combined_value_and_duty") && {
            codeChanges: watchedDutyAdjustments
              .filter((adj) => {
                if (adj.operation === "code_change") return adj.originalCode && adj.targetCode;
                else return adj.htsCode && adj.newRate !== undefined;
              })
              .map((adj) => {
                const baseEntry =
                  adj.operation === "code_change"
                    ? {
                        operation: adj.operation,
                        originalCode: adj.originalCode,
                        targetCode: adj.targetCode,
                        ch99ChangeMode: adj.ch99ChangeMode,
                        newCH99CodeList: adj.newCH99CodeList || [],
                        ch99Transformations: adj.ch99Transformations || [],
                      }
                    : {
                        operation: adj.operation,
                        htsCode: adj.htsCode,
                        newRate:
                          typeof adj.newRate === "string" ? parseFloat(adj.newRate) : adj.newRate,
                      };
                return {
                  ...baseEntry,
                  applyTo: adj.applyTo || "all",
                  targetEntryLines:
                    adj.applyTo === "specific"
                      ? adj.targetEntryLines || []
                      : lineAdjustments.map((la) => la.entryLine),
                };
              }),
          }),
          originalAdjustmentType: selectedAdjustmentType as
            | "value_percentage"
            | "duty_rate_adjustment"
            | "combined_value_and_duty",
        },
      };

      onSubmit(lineSpecificFormData);
      return;
    }

    // Bulk mode validation
    if (!dateRange.startDate || !dateRange.endDate) {
      setFieldErrors((prev) => ({ ...prev, dateRange: "Please select both start and end dates" }));
      return;
    }
    setFieldErrors((prev) => {
      const { dateRange, ...rest } = prev;
      return rest;
    });

    const hasHtsErrors = Object.values(htsValidationErrors).some((error) => error);
    if (hasHtsErrors) {
      showSnackbar("Validation Error", "Please fix HTS code validation errors before submitting", "error");
      return;
    }

    const watchedDutyAdjustments = watch("dutyAdjustments");

    if (data.adjustmentType === "duty_rate_adjustment") {
      if (!watchedDutyAdjustments || watchedDutyAdjustments.length === 0) {
        showSnackbar("Validation Error", "Please add at least one duty adjustment", "error");
        return;
      }
      const invalidAdjustments = watchedDutyAdjustments.filter((adj) => {
        if (adj.operation === "code_change") return !adj.originalCode || !adj.targetCode;
        else if (adj.operation === "rate_adjustment")
          return !adj.htsCode || adj.newRate === undefined || adj.newRate === null;
        return false;
      });
      if (invalidAdjustments.length > 0) {
        showSnackbar("Validation Error", "Please complete all required fields in duty adjustments", "error");
        return;
      }
    }

    if (data.adjustmentType === "combined_value_and_duty") {
      if (data.adjustmentMode === "percentage" && !data.percentage) {
        showSnackbar("Validation Error", "Please enter a value adjustment percentage", "error");
        return;
      }
      if (data.adjustmentMode === "amount" && (!data.amount || !data.currency)) {
        showSnackbar("Validation Error", "Please enter an adjustment amount and currency", "error");
        return;
      }
      if (watchedDutyAdjustments && watchedDutyAdjustments.length > 0) {
        const invalidAdjustments = watchedDutyAdjustments.filter((adj) => {
          if (adj.operation === "code_change") return !adj.originalCode || !adj.targetCode;
          else if (adj.operation === "rate_adjustment")
            return !adj.htsCode || adj.newRate === undefined || adj.newRate === null;
          return false;
        });
        if (invalidAdjustments.length > 0) {
          showSnackbar("Validation Error", "Please complete all required fields in duty adjustments", "error");
          return;
        }
      } else {
        data.dutyAdjustments = [];
      }
    }

    // Build HTS codes array
    const dutyHTSCodes: string[] = [];
    if (
      data.adjustmentType === "duty_rate_adjustment" ||
      data.adjustmentType === "combined_value_and_duty"
    ) {
      data.dutyAdjustments.forEach((adj) => {
        if (adj.operation === "code_change" && adj.originalCode) dutyHTSCodes.push(adj.originalCode);
        else if (adj.operation === "rate_adjustment" && adj.htsCode) dutyHTSCodes.push(adj.htsCode);
      });
    }

    const allHTSCodesCombined = [...(data.htsCodes || []), ...dutyHTSCodes];
    const uniqueHTSCodes = [...new Set(allHTSCodesCombined)];

    const finalFormData: ReconciliationFormData = {
      dateRange: {
        start: format(dateRange.startDate, "yyyy-MM-dd"),
        end: format(dateRange.endDate, "yyyy-MM-dd"),
      },
      filters: {
        reconFlag: data.reconFlag || undefined,
        htsCodes: uniqueHTSCodes.length > 0 ? uniqueHTSCodes : undefined,
        originCodes: data.originCodes.length > 0 ? data.originCodes : undefined,
        manufacturerIds: data.manufacturerIds.length > 0 ? data.manufacturerIds : undefined,
        entryLines: data.entryLines.length > 0 ? data.entryLines : undefined,
      },
      adjustmentType: data.adjustmentType,
      adjustmentData:
        data.adjustmentType === "value_percentage"
          ? {
              adjustmentMode: data.adjustmentMode,
              ...(data.adjustmentMode === "percentage"
                ? { percentage: data.percentage }
                : {
                    percentage: `${rateFactor}%`,
                    amount: parseFloat(data.amount),
                    currency: data.currency,
                    rateFactor: rateFactor,
                    totalSum: totalSum,
                  }),
            }
          : data.adjustmentType === "combined_value_and_duty"
          ? ({
              valueAdjustment: {
                adjustmentMode: data.adjustmentMode,
                ...(data.adjustmentMode === "percentage"
                  ? { percentage: data.percentage }
                  : {
                      percentage: `${rateFactor}%`,
                      amount: parseFloat(data.amount),
                      currency: data.currency,
                      rateFactor: rateFactor,
                      totalSum: totalSum,
                    }),
              },
              dutyRateAdjustment: {
                adjustments: data.dutyAdjustments
                  .filter((adj) => {
                    if (adj.operation === "code_change") return adj.originalCode && adj.targetCode;
                    else return adj.htsCode && adj.newRate !== undefined;
                  })
                  .map((adj) => {
                    if (adj.operation === "code_change") {
                      return {
                        operation: adj.operation,
                        originalCode: adj.originalCode,
                        targetCode: adj.targetCode,
                        ch99ChangeMode: adj.ch99ChangeMode,
                        newCH99CodeList: adj.newCH99CodeList || [],
                        ch99Transformations: adj.ch99Transformations || [],
                      };
                    } else {
                      return {
                        operation: adj.operation,
                        htsCode: adj.htsCode,
                        newRate:
                          typeof adj.newRate === "string" ? parseFloat(adj.newRate) : adj.newRate,
                      };
                    }
                  }),
              },
            } as CombinedAdjustmentData)
          : {
              adjustments: data.dutyAdjustments
                .filter((adj) => {
                  if (adj.operation === "code_change") return adj.originalCode && adj.targetCode;
                  else return adj.htsCode && adj.newRate !== undefined;
                })
                .map((adj) => {
                  if (adj.operation === "code_change") {
                    return {
                      operation: adj.operation,
                      originalCode: adj.originalCode,
                      targetCode: adj.targetCode,
                      ch99ChangeMode: adj.ch99ChangeMode,
                      newCH99CodeList: adj.newCH99CodeList || [],
                      ch99Transformations: adj.ch99Transformations || [],
                    };
                  } else {
                    return {
                      operation: adj.operation,
                      htsCode: adj.htsCode,
                      newRate:
                        typeof adj.newRate === "string" ? parseFloat(adj.newRate) : adj.newRate,
                    };
                  }
                }),
            },
    };

    const validationErrors = validateReconciliationForm(finalFormData);
    if (Object.keys(validationErrors).length > 0) {
      const errorMessages = Object.entries(validationErrors)
        .map(([field, errs]) => `${field}: ${errs.join(", ")}`)
        .join("; ");
      showSnackbar("Validation Error", errorMessages, "error");
      return;
    }

    onSubmit(finalFormData);
  };

  useEffect(() => {
    if (adjustmentType === "duty_rate_adjustment" && dutyAdjustments.length === 0) {
      addDutyAdjustment("rate_adjustment");
    }
  }, [adjustmentType, dutyAdjustments.length]);

  useEffect(() => {
    if (initialEntryLines && initialEntryLines.length > 0) {
      setValue("entryLines", initialEntryLines);
    }
  }, [initialEntryLines, setValue]);

  // ─── Render helpers for duty adjustment entries ──────────────────
  const renderDutyEntry = (entry: DutyAdjustmentEntry, index: number, isLineSpecific: boolean) => (
    <Box key={index} sx={{ p: 2, bgcolor: tokens.bg.surface, border: `1px solid ${tokens.border.default}`, borderRadius: tokens.radius.md }}>
      <Grid container spacing={2} alignItems="center">
        {/* Operation Type */}
        <Grid item xs={12} sm={isLineSpecific ? 2.5 : 3}>
          <SelectBox
            value={entry.operation}
            options={[
              { value: "code_change", label: "Change HTS Code" },
              { value: "rate_adjustment", label: "Adjust Duty Rate" },
            ]}
            label={isLineSpecific ? "Operation" : "Operation Type"}
            handleChangeValue={(value) => updateDutyAdjustment(index, "operation", value)}
          />
        </Grid>

        {/* Apply To (line-specific only) */}
        {isLineSpecific && (
          <Grid item xs={12} sm={2.5}>
            <SelectBox
              value={entry.applyTo || "all"}
              options={[
                { value: "all", label: `All ${lineAdjustments.length} entries` },
                { value: "specific", label: "Specific entries" },
              ]}
              label="Apply to"
              handleChangeValue={(value) => {
                updateDutyAdjustment(index, "applyTo", value);
                if (value === "all") updateDutyAdjustment(index, "targetEntryLines", []);
              }}
            />
          </Grid>
        )}

        {/* Code Change Fields */}
        {entry.operation === "code_change" && (
          <>
            <Grid item xs={12} sm={4}>
              <SingleAutocomplete
                label="Original Code"
                value={entry.originalCode || ""}
                options={htsOptions.map((opt) => opt.value)}
                onChange={(value) => updateDutyAdjustment(index, "originalCode", value)}
                placeholder={htsLoading ? "Loading..." : "Select or enter code"}
                disabled={htsLoading}
                helperText="Code to replace"
                error={!!htsValidationErrors[index]}
                customHeader={
                  <HTSCodeToggleHeader codeType={codeType} onCodeTypeChange={setCodeType} />
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <SingleAutocomplete
                label={isLineSpecific ? "New Code" : "Target Code"}
                value={entry.targetCode || ""}
                options={htsOptions.map((opt) => opt.value)}
                onChange={(value) => updateDutyAdjustment(index, "targetCode", value)}
                placeholder={htsLoading ? "Loading..." : "Select or enter code"}
                disabled={htsLoading}
                helperText="New HTS/CH99 code"
                customHeader={
                  <HTSCodeToggleHeader codeType={codeType} onCodeTypeChange={setCodeType} />
                }
              />
            </Grid>
          </>
        )}

        {/* Rate Adjustment Fields */}
        {entry.operation === "rate_adjustment" && (
          <>
            <Grid item xs={12} sm={4}>
              <SingleAutocomplete
                label="HTS Code"
                value={entry.htsCode || ""}
                options={htsOptions.map((opt) => opt.value)}
                onChange={(value) => updateDutyAdjustment(index, "htsCode", value)}
                placeholder={htsLoading ? "Loading..." : "Select or enter code"}
                disabled={htsLoading}
                helperText={htsValidationErrors[index] || "Code to modify rate"}
                error={!!htsValidationErrors[index]}
                customHeader={
                  <HTSCodeToggleHeader codeType={codeType} onCodeTypeChange={setCodeType} />
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextInput
                label="New Rate (0-10)"
                type="number"
                value={entry.newRate?.toString() || ""}
                handleChangeValue={(value) => {
                  const rate = parseFloat(value) || 0;
                  if (rate < 0 || rate > 10) {
                    setFieldErrors((prev) => ({
                      ...prev,
                      [`dutyRate_${index}`]: "Rate must be between 0 and 10",
                    }));
                  } else {
                    setFieldErrors((prev) => {
                      const { [`dutyRate_${index}`]: _, ...rest } = prev;
                      return rest;
                    });
                  }
                  updateDutyAdjustment(index, "newRate", rate);
                }}
                placeholder="e.g., 0.05 = 5%"
                helperText={fieldErrors[`dutyRate_${index}`] || "Decimal format: 0.05 = 5%"}
                error={!!fieldErrors[`dutyRate_${index}`]}
              />
            </Grid>
          </>
        )}

        {/* Remove Button */}
        <Grid item xs={12} sm={1}>
          <IconButton
            onClick={() => removeDutyAdjustment(index)}
            size="small"
            color="error"
            disabled={!isLineSpecific && adjustmentType === "duty_rate_adjustment" && dutyAdjustments.length === 1}
          >
            <CloseIcon />
          </IconButton>
        </Grid>

        {/* Entry Line Selection (line-specific, specific targets) */}
        {isLineSpecific && entry.applyTo === "specific" && (
          <Grid item xs={12}>
            <Box sx={{ mt: 1, p: 2, bgcolor: tokens.status.warningBg, borderRadius: 1, border: "1px solid #F59E0B" }}>
              <Typography variant="body2" fontWeight={tokens.fontWeight.bold} gutterBottom>
                Select Entries to Apply This Change
              </Typography>
              <MultiAutocomplete
                value={entry.targetEntryLines || []}
                onChange={(lines) => updateDutyAdjustment(index, "targetEntryLines", lines)}
                label="Target Entry Lines"
                options={lineAdjustments.map((adj) => adj.entryLine)}
                placeholder="Select entries..."
                helperText={`${entry.targetEntryLines?.length || 0} of ${lineAdjustments.length} entries selected`}
              />
            </Box>
          </Grid>
        )}

        {/* CH99 Options for Code Change */}
        {entry.operation === "code_change" && (
          <Grid item xs={12}>
            <Box sx={{ mt: 1, p: 2, bgcolor: tokens.bg.hover, borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={tokens.fontWeight.bold} gutterBottom>
                Chapter 99 Handling (Optional)
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={entry.ch99ChangeMode || "none"}
                  onChange={(e) => {
                    const mode =
                      e.target.value === "none" ? undefined : (e.target.value as CH99ChangeMode);
                    updateDutyAdjustment(index, "ch99ChangeMode", mode);
                    if (mode === undefined) {
                      updateDutyAdjustment(index, "newCH99CodeList", undefined);
                      updateDutyAdjustment(index, "ch99Transformations", undefined);
                    } else if (mode === "replace_all") {
                      updateDutyAdjustment(index, "ch99Transformations", undefined);
                      if (!entry.newCH99CodeList) updateDutyAdjustment(index, "newCH99CodeList", []);
                    } else if (mode === "selective") {
                      updateDutyAdjustment(index, "newCH99CodeList", undefined);
                      if (!entry.ch99Transformations) updateDutyAdjustment(index, "ch99Transformations", []);
                    }
                  }}
                  row
                >
                  <FormControlLabel value="none" control={<Radio size="small" />} label="No CH99 Changes" />
                  <FormControlLabel value="replace_all" control={<Radio size="small" />} label="Replace All CH99" />
                  <FormControlLabel value="selective" control={<Radio size="small" />} label="Selective" />
                </RadioGroup>
              </FormControl>

              {entry.ch99ChangeMode === "replace_all" && (
                <Box sx={{ mt: 2 }}>
                  <MultiAutocomplete
                    value={entry.newCH99CodeList || []}
                    onChange={(codes) => updateCH99CodeList(index, codes)}
                    label="New CH99 Code List"
                    options={ch99Codes.map((opt) => opt.value)}
                    placeholder="Select CH99 codes..."
                  />
                </Box>
              )}

              {entry.ch99ChangeMode === "selective" && (
                <Box sx={{ mt: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="caption" sx={{ color: tokens.text.secondary }}>
                      Transform specific CH99 codes
                    </Typography>
                    <Button size="small" onClick={() => addTransformation(index)}>
                      + Add
                    </Button>
                  </Stack>
                  {entry.ch99Transformations?.map((transform, tIdx) => (
                    <Grid container spacing={1} key={tIdx} sx={{ mb: 1 }}>
                      <Grid item xs={5}>
                        <SingleAutocomplete
                          label="From"
                          value={transform.from}
                          options={ch99Codes.map((opt) => opt.value)}
                          onChange={(value) => updateTransformation(index, tIdx, "from", value)}
                          placeholder="Original CH99"
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <SingleAutocomplete
                          label="To"
                          value={transform.to}
                          options={ch99Codes.map((opt) => opt.value)}
                          onChange={(value) => updateTransformation(index, tIdx, "to", value)}
                          placeholder="New CH99"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <IconButton
                          onClick={() => removeTransformation(index, tIdx)}
                          size="small"
                          color="error"
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}
                </Box>
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );

  // ─── JSX ─────────────────────────────────────────────────────────
  return (
    <Box component="form" id="config-form" onSubmit={handleSubmit(handleFormSubmit)}>
      <Stack spacing={3}>
        {/* Form Mode Selector */}
        <Box>
          {hasInitialItems && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{lineAdjustments.length} value discrepancies</strong> selected from verification.
                You can make line-specific adjustments or switch to bulk mode.
              </Typography>
            </Alert>
          )}
          <ModeToggle
            value={formMode}
            options={[
              { value: "line_specific", label: "Line-Specific", icon: <DescriptionIcon sx={{ fontSize: 16 }} /> },
              { value: "bulk", label: "Bulk Adjustment", icon: <SettingsIcon sx={{ fontSize: 16 }} /> },
            ]}
            onChange={(newMode) => setFormMode(newMode as FormMode)}
          />
        </Box>

        {/* ═══════════════ LINE-SPECIFIC MODE ═══════════════ */}
        {formMode === "line_specific" && (
          <Box sx={{ ...cardSx, overflow: "visible" }}>
              <SectionHeader icon={<SettingsIcon sx={{ fontSize: 16 }} />} label="Adjustment Type" />
              <Box sx={cardBodySx}>
                <Typography variant="body2" sx={{ color: tokens.text.secondary, mb: 2 }}>
                  Select the type of adjustment to apply to the {lineAdjustments.length} selected entry lines
                </Typography>
                <Controller
                  name="adjustmentType"
                  control={control}
                  render={({ field }) => (
                    <Stack direction="row" spacing={1.5}>
                      <AdjustmentTypeCard
                        symbol="$"
                        title="Value Adjustment"
                        description="Adjust declared customs value"
                        selected={field.value === "value_percentage"}
                        onClick={() => { field.onChange("value_percentage"); setValue("dutyAdjustments", []); }}
                      />
                      <AdjustmentTypeCard
                        symbol="%"
                        title="Code Change Only"
                        description="Correct duty rate or tariff classification"
                        selected={field.value === "duty_rate_adjustment"}
                        onClick={() => field.onChange("duty_rate_adjustment")}
                      />
                      <AdjustmentTypeCard
                        symbol="$+%"
                        title="Value + Code Change"
                        description="Adjust both value and duty rate"
                        selected={field.value === "combined_value_and_duty"}
                        onClick={() => field.onChange("combined_value_and_duty")}
                      />
                    </Stack>
                  )}
                />
              </Box>

            {(adjustmentType === "value_percentage" || adjustmentType === "combined_value_and_duty") && (
              <>
                <Box sx={{ borderTop: sectionBreak }}>
                <SectionHeader
                  icon={<ListIcon sx={{ fontSize: 16 }} />}
                  label={adjustmentType === "combined_value_and_duty" ? "Value Adjustments" : "Line Adjustments"}
                  actions={
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={addLineAdjustment}>
                      Add Line
                    </Button>
                    {lineAdjustments.length > 0 && (
                      <>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="caption" sx={{ color: tokens.text.secondary }}>Lines</Typography>
                          <Typography variant="h6" fontWeight={tokens.fontWeight.bold}>{lineAdjustmentTotals.count}</Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="caption" sx={{ color: tokens.text.secondary }}>Net Adjustment</Typography>
                          <Typography
                            variant="h6"
                            fontWeight={tokens.fontWeight.bold}
                            sx={{ color: lineAdjustmentTotals.totalAdjustment >= 0 ? tokens.status.danger : tokens.status.success }}
                          >
                            {lineAdjustmentTotals.totalAdjustment >= 0 ? "+" : ""}${numberWithCommas(Math.abs(lineAdjustmentTotals.totalAdjustment))}
                          </Typography>
                        </Box>
                      </>
                    )}
                  </Stack>
                  }
                />
                </Box>
                <Box sx={cardBodySx}>
                  {lineAdjustments.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 4, bgcolor: tokens.bg.surface, borderRadius: 1, border: `1px dashed ${tokens.border.default}` }}>
                      <Typography variant="body2" sx={{ color: tokens.text.secondary, mb: 2 }}>
                        No entry lines added yet. Click "Add Line" to specify individual adjustments.
                      </Typography>
                      <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={addLineAdjustment}>
                        Add First Line
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                      <Stack spacing={1}>
                        {/* Table Header */}
                        <Grid container spacing={1} sx={{ px: 2, py: 1, bgcolor: tokens.bg.muted, borderRadius: 1 }}>
                          <Grid item xs={2.5}><Typography variant="caption" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.secondary }}>Entry / Line</Typography></Grid>
                          <Grid item xs={1.5}><Typography variant="caption" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.secondary }}>Type</Typography></Grid>
                          <Grid item xs={2}><Typography variant="caption" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.secondary }} align="right">Declared</Typography></Grid>
                          <Grid item xs={2}><Typography variant="caption" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.secondary }} align="right">Corrected</Typography></Grid>
                          <Grid item xs={2.5}><Typography variant="caption" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.secondary }} align="right">Adjustment</Typography></Grid>
                          <Grid item xs={1.5}><Typography variant="caption" fontWeight={tokens.fontWeight.bold} sx={{ color: tokens.text.secondary }} align="center">Actions</Typography></Grid>
                        </Grid>

                        {/* Table Rows */}
                        {lineAdjustments.map((adj, index) => {
                          const isManuallyAdded = adj.entryLine.startsWith("NEW-");
                          return (
                            <Grid
                              key={adj.entryLine}
                              container
                              spacing={1}
                              sx={{
                                px: 2, py: 1, bgcolor: tokens.bg.surface, borderRadius: 1,
                                border: "1px solid", borderColor: adj.isEdited ? tokens.brand.primary : tokens.border.default,
                                "&:hover": { borderColor: tokens.brand.primary },
                              }}
                              alignItems="center"
                            >
                              <Grid item xs={2.5}>
                                {isManuallyAdded ? (
                                  <Stack spacing={0.5}>
                                    <TextField size="small" placeholder="Entry #" value={adj.entryNumber || ""}
                                      onChange={(e) => {
                                        const updated = [...lineAdjustments];
                                        updated[index] = { ...updated[index], entryNumber: e.target.value, isEdited: true };
                                        setLineAdjustments(updated);
                                      }}
                                      sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.base } }}
                                    />
                                    <TextField size="small" placeholder="Line #" type="number" value={adj.lineNumber || ""}
                                      onChange={(e) => {
                                        const updated = [...lineAdjustments];
                                        updated[index] = { ...updated[index], lineNumber: parseInt(e.target.value) || undefined, isEdited: true };
                                        setLineAdjustments(updated);
                                      }}
                                      sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.base } }}
                                    />
                                  </Stack>
                                ) : (
                                  <>
                                    <Typography variant="body2" fontWeight={tokens.fontWeight.semibold}>{adj.entryNumber || "-"}</Typography>
                                    <Typography variant="caption" sx={{ color: tokens.text.secondary }}>Line {adj.lineNumber || "-"}</Typography>
                                  </>
                                )}
                              </Grid>
                              <Grid item xs={1.5}>
                                <Chip
                                  size="small"
                                  icon={adj.adjustmentType === "UNDERVALUATION" ? <UndervalIcon sx={{ fontSize: tokens.fontSize.lg }} /> : <OvervalIcon sx={{ fontSize: tokens.fontSize.lg }} />}
                                  label={adj.adjustmentType === "UNDERVALUATION" ? "Under" : adj.adjustmentType === "OVERVALUATION" ? "Over" : "Mismatch"}
                                  sx={{
                                    bgcolor: adj.adjustmentType === "UNDERVALUATION" ? tokens.status.dangerBg : adj.adjustmentType === "OVERVALUATION" ? tokens.status.warningBg : tokens.chip.indigo.bg,
                                    color: adj.adjustmentType === "UNDERVALUATION" ? tokens.status.danger : adj.adjustmentType === "OVERVALUATION" ? tokens.status.warning : tokens.chip.indigo.text,
                                    fontSize: tokens.fontSize.xs,
                                  }}
                                />
                              </Grid>
                              <Grid item xs={2}>
                                {isManuallyAdded ? (
                                  <TextField size="small" type="number" placeholder="Original" value={adj.originalValue || ""}
                                    onChange={(e) => {
                                      const newOriginal = parseFloat(e.target.value) || 0;
                                      const updated = [...lineAdjustments];
                                      updated[index] = { ...updated[index], originalValue: newOriginal, adjustmentAmount: updated[index].correctValue - newOriginal, isEdited: true };
                                      setLineAdjustments(updated);
                                    }}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.base }, bgcolor: tokens.bg.surface }}
                                    fullWidth
                                  />
                                ) : (
                                  <Typography variant="body2" align="right" sx={{ color: tokens.text.secondary }}>${numberWithCommas(adj.originalValue)}</Typography>
                                )}
                              </Grid>
                              <Grid item xs={2}>
                                <TextField size="small" type="number" value={adj.correctValue}
                                  onChange={(e) => updateLineAdjustment(index, "correctValue", e.target.value)}
                                  InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                  sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.base }, bgcolor: tokens.bg.surface }}
                                  fullWidth
                                />
                              </Grid>
                              <Grid item xs={2.5}>
                                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1}>
                                  <TextField size="small" type="number" value={adj.adjustmentAmount}
                                    onChange={(e) => updateLineAdjustment(index, "adjustmentAmount", e.target.value)}
                                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                                    sx={{ width: 100, "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.base }, bgcolor: tokens.bg.surface }}
                                  />
                                  {adj.isEdited && (
                                    <Chip size="small" label="Edited" sx={{ bgcolor: tokens.chip.indigo.bg, color: tokens.chip.indigo.text, fontSize: tokens.fontSize["2xs"], height: 20 }} />
                                  )}
                                </Stack>
                              </Grid>
                              <Grid item xs={1.5}>
                                <Stack direction="row" justifyContent="center">
                                  <IconButton size="small" onClick={() => removeLineAdjustment(index)} sx={{ color: tokens.status.danger, "&:hover": { bgcolor: tokens.status.dangerBg } }}>
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Stack>
                              </Grid>
                            </Grid>
                          );
                        })}
                      </Stack>
                    </Box>
                  )}

                  {/* Summary Row */}
                  {lineAdjustments.length > 0 && (
                    <Box sx={{ mt: 2, bgcolor: tokens.bg.muted, p: 2, borderRadius: 1, border: `1px solid ${tokens.border.default}` }}>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <Typography variant="caption" sx={{ color: tokens.text.secondary }}>Total Original</Typography>
                          <Typography variant="body1" fontWeight={tokens.fontWeight.bold}>${numberWithCommas(lineAdjustmentTotals.totalOriginal)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" sx={{ color: tokens.text.secondary }}>Total Corrected</Typography>
                          <Typography variant="body1" fontWeight={tokens.fontWeight.bold}>${numberWithCommas(lineAdjustmentTotals.totalCorrected)}</Typography>
                        </Grid>
                        <Grid item xs={4}>
                          <Typography variant="caption" sx={{ color: tokens.text.secondary }}>Total Adjustment</Typography>
                          <Typography variant="body1" fontWeight={tokens.fontWeight.bold}
                            sx={{ color: lineAdjustmentTotals.totalAdjustment >= 0 ? tokens.status.danger : tokens.status.success }}
                          >
                            {lineAdjustmentTotals.totalAdjustment >= 0 ? "+" : ""}${numberWithCommas(Math.abs(lineAdjustmentTotals.totalAdjustment))}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </Box>
              </>
            )}

            {(adjustmentType === "duty_rate_adjustment" || adjustmentType === "combined_value_and_duty") && (
              <>
                <Box sx={{ borderTop: sectionBreak }}>
                <SectionHeader
                  icon={<EditIcon sx={{ fontSize: 16 }} />}
                  label="Code Changes"
                  actions={
                    <Stack direction="row" spacing={1}>
                      <Button variant="outlined" size="small" onClick={() => addDutyAdjustment("code_change")}>+ HTS Code Change</Button>
                      <Button variant="outlined" size="small" onClick={() => addDutyAdjustment("rate_adjustment")}>+ Rate Adjustment</Button>
                    </Stack>
                  }
                />
                </Box>
                <Box sx={cardBodySx}>
                  <Stack spacing={2}>
                    {dutyAdjustments.map((entry, index) => renderDutyEntry(entry, index, true))}
                    {dutyAdjustments.length === 0 && (
                      <Typography variant="body2" sx={{ color: tokens.text.secondary, textAlign: "center", py: 2 }}>
                        No code changes added. Click a button above to add one.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </>
            )}

              <Box sx={{ borderTop: sectionBreak }}>
                <SectionHeader icon={<InfoIcon sx={{ fontSize: 16 }} />} label="Summary" />
              </Box>
              <Box sx={cardBodySx}>
                <Stack spacing={1}>
                  <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                    <strong>Selected Lines:</strong> {lineAdjustments.length} entry lines
                  </Typography>
                  {(adjustmentType === "value_percentage" || adjustmentType === "combined_value_and_duty") && (
                    <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                      <strong>Value Adjustment:</strong> {lineAdjustmentTotals.totalAdjustment >= 0 ? "+" : ""}${numberWithCommas(Math.abs(lineAdjustmentTotals.totalAdjustment))} (proportionally distributed)
                    </Typography>
                  )}
                  {(adjustmentType === "duty_rate_adjustment" || adjustmentType === "combined_value_and_duty") && dutyAdjustments.length > 0 && (
                    <Box>
                      <Typography variant="body2" sx={{ color: tokens.text.secondary }} gutterBottom><strong>Code Changes:</strong></Typography>
                      {dutyAdjustments.map((adj, idx) => (
                        <Typography key={idx} variant="caption" display="block" sx={{ color: tokens.text.secondary, ml: 2 }}>
                          {"\u2022 "}
                          {adj.operation === "code_change"
                            ? `${formatHTSCode(adj.originalCode ?? "")} \u2192 ${formatHTSCode(adj.targetCode ?? "")}`
                            : `${formatHTSCode(adj.htsCode ?? "")} rate \u2192 ${adj.newRate}`}
                          {" "}
                          <Chip
                            size="small"
                            label={adj.applyTo === "specific" ? `${adj.targetEntryLines?.length || 0} entries` : "All entries"}
                            sx={{
                              height: 16, fontSize: tokens.fontSize["2xs"],
                              bgcolor: adj.applyTo === "specific" ? tokens.status.warningBg : tokens.chip.indigo.bg,
                              color: adj.applyTo === "specific" ? tokens.chip.amber.text : tokens.chip.indigo.text,
                            }}
                          />
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Stack>
              </Box>
          </Box>
        )}

        {/* ═══════════════ BULK MODE ═══════════════ */}
        {formMode === "bulk" && (
          <>
          {/* Adjustment Type Card */}
          <Box sx={cardSx}>
              <SectionHeader icon={<SettingsIcon sx={{ fontSize: 16 }} />} label="Adjustment Type" />
              <Box sx={cardBodySx}>
                <Controller
                  name="adjustmentType"
                  control={control}
                  render={({ field }) => (
                    <Stack direction="row" spacing={1.5}>
                      <AdjustmentTypeCard
                        symbol="$"
                        title="Value Adjustment"
                        description="Adjust the declared customs value of imported goods"
                        selected={field.value === "value_percentage"}
                        onClick={() => { field.onChange("value_percentage"); }}
                      />
                      <AdjustmentTypeCard
                        symbol="%"
                        title="Duty Rate Adjustment"
                        description="Correct the applied duty rate or tariff classification"
                        selected={field.value === "duty_rate_adjustment"}
                        onClick={() => { field.onChange("duty_rate_adjustment"); setValue("htsCodes", []); }}
                      />
                      <AdjustmentTypeCard
                        symbol="$+%"
                        title="Combined Adjustment"
                        description="Adjust both value and duty rate simultaneously"
                        selected={field.value === "combined_value_and_duty"}
                        onClick={() => { field.onChange("combined_value_and_duty"); setValue("htsCodes", []); }}
                      />
                    </Stack>
                  )}
                />
              </Box>
          </Box>

          {/* Date Range Card */}
          <Box sx={{ ...cardSx, overflow: "visible" }}>
              <SectionHeader icon={<CalendarIcon sx={{ fontSize: 16 }} />} label="Date Range" />
              <Box sx={cardBodySx}>
                <DateRangeFilter
                  value={dateRange}
                  onSelectDate={(range) => {
                    setDateRange(range);
                    if (range.startDate && range.endDate) {
                      setFieldErrors((prev) => {
                        const { dateRange: _, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                />
                {fieldErrors.dateRange && (
                  <Typography variant="caption" color="error" sx={{ mt: 1, display: "block" }}>{fieldErrors.dateRange}</Typography>
                )}
              </Box>
          </Box>

          {/* Filters Card */}
          <Box sx={cardSx}>
              <SectionHeader icon={<FilterListIcon sx={{ fontSize: 16 }} />} label="Filters" suffix="(Optional)" />
              <Box sx={cardBodySx}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="reconFlag"
                      control={control}
                      render={({ field }) => (
                        <SelectBox
                          value={field.value}
                          options={[{ value: "", label: "All entries" }, ...reconFlagOptions]}
                          label="Reconciliation Status"
                          handleChangeValue={field.onChange}
                          error={!!errors.reconFlag}
                          helperText={errors.reconFlag?.message}
                          placeholder="All entries"
                        />
                      )}
                    />
                  </Grid>

                  {adjustmentType === "value_percentage" ? (
                    <Grid item xs={12}>
                      <Controller
                        name="htsCodes"
                        control={control}
                        render={({ field }) => (
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="flex-start">
                              <Box flex={1}>
                                {htsLoading ? (
                                  <SkeletonLoader type="form-field" />
                                ) : (
                                  <HTSCodesWithToggle
                                    value={field.value}
                                    onChange={field.onChange}
                                    codeType={codeType}
                                    onCodeTypeChange={setCodeType}
                                    allOptions={allHTSCodes}
                                    isLoading={htsLoading}
                                  />
                                )}
                              </Box>
                              <Tooltip title="Filter by HTS codes. Supports wildcards (e.g., 1234* for all codes starting with 1234). Chapter 99 codes are included.">
                                <InfoIcon color="action" fontSize="small" sx={{ mt: 2 }} />
                              </Tooltip>
                            </Stack>
                          </Box>
                        )}
                      />
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Box sx={{ p: 2, bgcolor: tokens.status.infoBg, borderRadius: 1 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <InfoIcon color="info" fontSize="small" />
                          <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                            HTS code filtering is not available for duty rate adjustments as the specific codes are defined in the adjustment parameters below.
                          </Typography>
                        </Stack>
                      </Box>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="originCodes"
                      control={control}
                      render={({ field }) => (
                        <MultiAutocomplete
                          value={field.value}
                          onChange={field.onChange}
                          label="Origin Countries"
                          options={countryOptions.map((opt) => opt.value)}
                          placeholder="Select countries..."
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Controller
                      name="manufacturerIds"
                      control={control}
                      render={({ field }) => (
                        <MultiAutocomplete
                          value={field.value}
                          onChange={field.onChange}
                          label="Manufacturers"
                          options={manufacturerOptions.map((opt) => opt.value)}
                          placeholder="Select manufacturers..."
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="entryLines"
                      control={control}
                      render={({ field }) => (
                        <Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="subtitle2" fontWeight={tokens.fontWeight.bold}>
                                Entry Summary + Line Numbers
                              </Typography>
                              <Tooltip title="Filter by specific entry summary + line numbers. Paste a list from Excel or enter manually.">
                                <InfoIcon color="action" fontSize="small" />
                              </Tooltip>
                            </Stack>
                            <Button size="small" variant="text" onClick={() => field.onChange([])} disabled={!field.value || field.value.length === 0} sx={{ color: tokens.text.secondary }}>
                              Clear All
                            </Button>
                          </Stack>
                          <MultiAutocomplete
                            value={[]}
                            inputValue={entryLineInputValue}
                            onInputChange={(_, newValue) => setEntryLineInputValue(newValue)}
                            onChange={(newValue) => {
                              const existingSet = new Set(field.value);
                              const newLines = newValue.filter((v: string) => !existingSet.has(v));
                              if (newLines.length > 0) field.onChange([...field.value, ...newLines]);
                            }}
                            label="Entry Summary + Line Numbers"
                            options={[]}
                            placeholder="Paste or enter entry summary+line numbers..."
                            onPaste={(event: React.ClipboardEvent<HTMLDivElement>) => {
                              event.preventDefault();
                              event.stopPropagation();
                              const pastedText = event.clipboardData.getData("text");
                              const entryLineNumbers = pastedText
                                .split(/[\s,\n\r\t;]+/)
                                .map((line) => line.trim())
                                .filter((line) => line && line.length > 0);
                              if (entryLineNumbers.length > 0) {
                                const existingSet = new Set(field.value);
                                const newLines = entryLineNumbers.filter((line) => !existingSet.has(line));
                                if (newLines.length > 0) field.onChange([...field.value, ...newLines]);
                                setTimeout(() => setEntryLineInputValue(""), 50);
                              }
                            }}
                            renderTags={() => null}
                          />
                          <Box sx={{ mt: 1, border: `1px solid ${tokens.border.default}`, borderRadius: "4px", overflow: "hidden" }}>
                            <VirtualizedChipList
                              key={`entry-lines-${field.value.length}`}
                              items={field.value}
                              onDeleteItem={(lineToDelete: any) => {
                                const newLines = field.value.filter((l: any) => l !== lineToDelete);
                                field.onChange(newLines);
                              }}
                            />
                          </Box>
                        </Box>
                      )}
                    />
                  </Grid>
                </Grid>
              </Box>
          </Box>

          {/* Adjustment Parameters Card */}
          <Box sx={cardSx}>
              <SectionHeader icon={<DescriptionIcon sx={{ fontSize: 16 }} />} label="Adjustment Parameters" />
              <Box sx={cardBodySx}>
                {/* Value Percentage */}
                {adjustmentType === "value_percentage" && (
                  <Stack spacing={2}>
                    <Typography variant="subtitle2" fontWeight={tokens.fontWeight.semibold}>Value Adjustment</Typography>
                    <Controller
                      name="adjustmentMode"
                      control={control}
                      render={({ field }) => (
                        <SegmentedToggle
                          value={field.value}
                          options={adjustmentModeOptions}
                          onChange={field.onChange}
                        />
                      )}
                    />

                    {adjustmentMode === "percentage" && (
                      <Stack spacing={3} sx={{ width: "100%", mb: 3 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Controller
                              name="percentage"
                              control={control}
                              rules={{
                                required: adjustmentMode === "percentage" ? "Percentage is required" : false,
                                pattern: { value: /^-?\d+(\.\d+)?%$/, message: "Must be a percentage (e.g., 5% or -10.5%)" },
                              }}
                              render={({ field }) => (
                                <Box>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <TextInput
                                      label="Value Adjustment Percentage"
                                      type="text"
                                      value={field.value}
                                      handleChangeValue={(value) => { field.onChange(value); debouncedValidate("percentage", value); }}
                                      error={!!errors.percentage || !!fieldErrors.percentage}
                                      placeholder="e.g., 5% or 0.1%"
                                      helperText={fieldErrors.percentage || "Enter percentage adjustment"}
                                    />
                                    <Tooltip title="Examples: 5% for 5 percent increase, -2.5% for decrease">
                                      <InfoIcon color="action" fontSize="small" />
                                    </Tooltip>
                                  </Stack>
                                  <InputErrorMessage message={errors.percentage?.message || fieldErrors.percentage} />
                                </Box>
                              )}
                            />
                          </Grid>
                        </Grid>

                        {percentage && (
                          <>
                            <StyledInputContainer>
                              <Typography variant="body1">Total Value of Flagged Transactions</Typography>
                              <Typography variant="body1">
                                {totalSumLoading ? <SkeletonLoader width={100} height={24} /> : `${getCurrencySymbol(currency)}${totalSum ? numberWithCommas(totalSum) : "0.00"}`}
                              </Typography>
                            </StyledInputContainer>
                            <StyledInputContainer>
                              <Typography variant="body1">Adjustment Amount ({percentage})</Typography>
                              <Typography variant="body1">
                                {totalSumLoading ? <SkeletonLoader width={120} height={24} /> : `${getCurrencySymbol(currency)}${adjustmentAmount ? numberWithCommas(Math.abs(adjustmentAmount)) : "0.00"}${adjustmentAmount < 0 ? " (decrease)" : " (increase)"}`}
                              </Typography>
                            </StyledInputContainer>
                          </>
                        )}

                        {!totalSumLoading && (!totalSum || totalSum === 0) && percentage && currency && dateRange.startDate && dateRange.endDate && (
                          <Typography variant="body2" sx={{ color: tokens.status.warning, textAlign: "center", mt: 2 }}>
                            No reconciliation data found for the selected criteria
                          </Typography>
                        )}
                      </Stack>
                    )}

                    {adjustmentMode === "amount" && (
                      <Stack spacing={3} sx={{ width: "100%", mb: 3 }}>
                        <StyledInputContainer>
                          <Typography variant="body1">Transfer Price Adjustment Figure</Typography>
                          <Box sx={{ display: "flex", maxWidth: 250 }}>
                            <Controller
                              name="currency"
                              control={control}
                              render={({ field }) => (
                                <SelectBox
                                  value={field.value}
                                  options={currencyOptions.map((opt) => ({ value: opt.value, label: `${getCurrencySymbol(opt.value)}${opt.value}` }))}
                                  handleChangeValue={field.onChange}
                                  sx={{ minWidth: 95, borderRight: `1px solid ${tokens.border.default}`, "& .MuiOutlinedInput-root": { borderRadius: "4px 0 0 4px" } }}
                                />
                              )}
                            />
                            <Controller
                              name="amount"
                              control={control}
                              rules={{
                                required: adjustmentMode === "amount" ? "Amount is required" : false,
                                pattern: { value: /^-?\d+(\.\d+)?$/, message: "Must be a valid number" },
                              }}
                              render={({ field }) => (
                                <TextInput
                                  type="number"
                                  value={field.value}
                                  handleChangeValue={(value) => { field.onChange(value); debouncedValidate("amount", value); }}
                                  placeholder="0.00"
                                  error={!!fieldErrors.amount}
                                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "0 4px 4px 0", paddingLeft: 0, border: `1px solid ${tokens.border.default}` } }}
                                />
                              )}
                            />
                          </Box>
                        </StyledInputContainer>

                        <StyledInputContainer>
                          <Typography variant="body1">Entered Value of Flagged Transactions</Typography>
                          <Typography variant="body1">
                            {totalSumLoading ? <SkeletonLoader width={100} height={24} /> : `${getCurrencySymbol(currency)}${totalSum ? numberWithCommas(totalSum) : "0.00"}`}
                          </Typography>
                        </StyledInputContainer>

                        <StyledInputContainer>
                          <Typography variant="body1">Adjustment Factor</Typography>
                          <Typography variant="body1">{rateFactor}%</Typography>
                        </StyledInputContainer>

                        {!totalSumLoading && (!totalSum || totalSum === 0) && amount && currency && dateRange.startDate && dateRange.endDate && (
                          <Typography variant="body2" sx={{ color: tokens.status.warning, textAlign: "center", mt: 2 }}>
                            No reconciliation data found for the selected criteria
                          </Typography>
                        )}
                      </Stack>
                    )}
                  </Stack>
                )}

                {/* Combined Value and Duty */}
                {adjustmentType === "combined_value_and_duty" && (
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="subtitle1" gutterBottom sx={{ color: tokens.brand.primary, fontWeight: "bold" }}>
                        Step 1: Value Adjustment
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Controller
                            name="adjustmentMode"
                            control={control}
                            render={({ field }) => (
                              <Box>
                                <Typography variant="subtitle2" gutterBottom sx={{ color: tokens.text.secondary }}>Adjustment Mode</Typography>
                                <SegmentedToggle
                                  value={field.value}
                                  options={adjustmentModeOptions}
                                  onChange={(val) => {
                                    field.onChange(val);
                                    if (val === "percentage") { setValue("amount", ""); setValue("currency", "USD"); }
                                    else { setValue("percentage", ""); }
                                  }}
                                />
                              </Box>
                            )}
                          />
                        </Grid>

                        {adjustmentMode === "percentage" && (
                          <Grid item xs={12} sm={6}>
                            <Controller
                              name="percentage"
                              control={control}
                              rules={{
                                required: adjustmentType === "combined_value_and_duty" && adjustmentMode === "percentage" ? "Percentage is required" : false,
                                pattern: { value: /^-?\d+(\.\d+)?%$/, message: "Must be a percentage (e.g., 5% or -10.5%)" },
                              }}
                              render={({ field }) => (
                                <Box>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <TextInput
                                      label="Value Adjustment Percentage"
                                      type="text"
                                      value={field.value}
                                      handleChangeValue={(value) => { field.onChange(value); debouncedValidate("percentage", value); }}
                                      error={!!errors.percentage || !!fieldErrors.percentage}
                                      placeholder="e.g., 5% or 0.1%"
                                      helperText={fieldErrors.percentage || "Enter percentage adjustment"}
                                    />
                                    <Tooltip title="This percentage will be applied to increase the value before duty recalculation">
                                      <InfoIcon color="action" fontSize="small" />
                                    </Tooltip>
                                  </Stack>
                                  <InputErrorMessage message={errors.percentage?.message || fieldErrors.percentage} />
                                </Box>
                              )}
                            />
                          </Grid>
                        )}

                        {adjustmentMode === "amount" && (
                          <>
                            <Grid item xs={12} sm={4}>
                              <Controller
                                name="amount"
                                control={control}
                                rules={{
                                  required: adjustmentType === "combined_value_and_duty" && adjustmentMode === "amount" ? "Amount is required" : false,
                                  pattern: { value: /^\d+(\.\d{1,2})?$/, message: "Must be a valid amount (e.g., 100 or 100.50)" },
                                }}
                                render={({ field }) => (
                                  <Box>
                                    <TextInput
                                      label="Adjustment Amount"
                                      type="text"
                                      value={field.value}
                                      handleChangeValue={(value) => { field.onChange(value); debouncedValidate("amount", value); }}
                                      error={!!errors.amount || !!fieldErrors.amount}
                                      placeholder="e.g., 100000"
                                      helperText={fieldErrors.amount || "Total value to be adjusted"}
                                    />
                                    <InputErrorMessage message={errors.amount?.message || fieldErrors.amount} />
                                  </Box>
                                )}
                              />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                              <Controller
                                name="currency"
                                control={control}
                                render={({ field }) => (
                                  <SelectBox value={field.value} options={currencyOptions} label="Currency" handleChangeValue={field.onChange} />
                                )}
                              />
                            </Grid>
                          </>
                        )}
                      </Grid>

                      {((adjustmentMode === "percentage" && percentage) || (adjustmentMode === "amount" && amount && totalSum && totalSum > 0)) && (
                        <Stack spacing={2} sx={{ mt: 2 }}>
                          <StyledInputContainer>
                            <Typography variant="body1">Total Value of Selected Transactions</Typography>
                            <Typography variant="body1">
                              {totalSumLoading ? <SkeletonLoader width={100} height={24} /> : `${getCurrencySymbol("USD")}${totalSum ? numberWithCommas(totalSum) : "0.00"}`}
                            </Typography>
                          </StyledInputContainer>
                          {adjustmentMode === "percentage" && (
                            <StyledInputContainer>
                              <Typography variant="body1">Value Increase ({percentage})</Typography>
                              <Typography variant="body1">
                                {totalSumLoading ? <SkeletonLoader width={120} height={24} /> : `${getCurrencySymbol("USD")}${adjustmentAmount ? numberWithCommas(Math.abs(adjustmentAmount)) : "0.00"}`}
                              </Typography>
                            </StyledInputContainer>
                          )}
                          {adjustmentMode === "amount" && (
                            <>
                              <StyledInputContainer>
                                <Typography variant="body1">Adjustment Amount</Typography>
                                <Typography variant="body1">{`${getCurrencySymbol(currency)}${amount ? numberWithCommas(parseFloat(amount) || 0) : "0.00"}`}</Typography>
                              </StyledInputContainer>
                              <StyledInputContainer>
                                <Typography variant="body1">Rate Factor</Typography>
                                <Typography variant="body1">{rateFactor}%</Typography>
                              </StyledInputContainer>
                            </>
                          )}
                        </Stack>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="subtitle1" gutterBottom sx={{ color: tokens.brand.primary, fontWeight: "bold" }}>
                        Step 2: Duty Rate Adjustments (Applied to Increased Values)
                      </Typography>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="body2" color="textSecondary">
                          These duty rate changes will be applied to the increased values from Step 1
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Button size="small" variant="outlined" onClick={() => addDutyAdjustment("code_change")}>+ Code Change</Button>
                          <Button size="small" variant="outlined" onClick={() => addDutyAdjustment("rate_adjustment")}>+ Rate Adjustment</Button>
                        </Stack>
                      </Stack>

                      <Stack spacing={2}>
                        {dutyAdjustments.map((entry, index) => renderDutyEntry(entry, index, false))}
                        {dutyAdjustments.length === 0 && (
                          <Typography variant="body2" color="textSecondary" sx={{ textAlign: "center", py: 2 }}>
                            No duty adjustments added. Click "+ Code Change" or "+ Rate Adjustment" to add one.
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                  </Stack>
                )}

                {/* Duty Rate Adjustment Only */}
                {adjustmentType === "duty_rate_adjustment" && (
                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1">Duty Adjustments</Typography>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" onClick={() => addDutyAdjustment("code_change")}>+ Code Change</Button>
                        <Button size="small" variant="outlined" onClick={() => addDutyAdjustment("rate_adjustment")}>+ Rate Adjustment</Button>
                      </Stack>
                    </Stack>

                    <Stack spacing={2}>
                      {dutyAdjustments.map((entry, index) => renderDutyEntry(entry, index, false))}
                    </Stack>

                    <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: "block" }}>
                      <strong>Code Changes:</strong> Replace MFN HTS codes across matching records. Optionally modify CH99 codes.{" "}
                      <strong>Rate Adjustments:</strong> Modify duty rates for specific HTS codes. Both support wildcards.
                    </Typography>
                  </Box>
                )}
              </Box>
          </Box>

          {/* Bottom Status Bar */}
          {totalSum !== undefined && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                px: 2.5,
                py: 1.5,
                bgcolor: tokens.bg.surface,
                border: `1px solid ${tokens.border.default}`,
                borderRadius: tokens.radius.lg,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <InfoIcon sx={{ fontSize: 16, color: tokens.text.muted }} />
                <Typography variant="body2" sx={{ color: tokens.text.secondary }}>
                  <strong style={{ color: tokens.text.primary }}>{totalSum ? numberWithCommas(Math.round(totalSum)) : "0"}</strong> entries match your current filters
                </Typography>
              </Stack>
            </Box>
          )}
          </>
        )}

        {/* Submit is triggered externally via form="config-form" */}
      </Stack>
    </Box>
  );
};

export default StepConfigure;
